import {
  HelpOutline,
  Logout,
  Person,
} from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  CssBaseline,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useContact } from '../../hooks/data';
import { IConfig } from '../../Interfaces/IConfig';
import { HelpOverview } from './LegalTransaction/HelpOverview';

const computeInitial = (
  firstName?: string,
  lastName?: string,
  fallback = ''
): string => {
  const first = firstName?.trim()[0] ?? '';
  const last = lastName?.trim()[0] ?? '';
  const combined = `${first}${last}`;
  if (combined.length > 0) return combined.toUpperCase();
  return fallback.slice(0, 1).toUpperCase();
};

interface IProps {
  children: ReactNode;
  configObject: IConfig;
}

export default function Layout({ children, configObject }: IProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { contact } = useContact();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const handleLogout = () => {
    logout();
  };

  const closeMenu = () => setMenuAnchor(null);

  const avatarInitial = computeInitial(
    contact?.FirstName,
    contact?.LastName,
    configObject.company_name
  );

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar position='fixed'>
        <Toolbar sx={{ minHeight: 72, px: { xs: 2, md: 4 } }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant='h6' noWrap>
              {configObject.company_name}
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1 }}>
              Mandantenportal
            </Typography>
          </Box>
          <Chip
            label={`Version ${configObject.version}`}
            sx={{
              mr: 2,
              display: { xs: 'none', sm: 'flex' },
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
              color: 'text.primary',
              fontWeight: 600,
            }}
          />
          <IconButton
            onClick={e => setMenuAnchor(e.currentTarget)}
            aria-label='Benutzermenü öffnen'
            sx={{ p: 0 }}
          >
            <Avatar
              sx={{
                width: 38,
                height: 38,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                fontWeight: 700,
                fontSize: avatarInitial.length > 1 ? '0.85rem' : '1rem',
                border: `1.5px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                transition: 'box-shadow 0.15s ease',
                '&:hover': {
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
              }}
            >
              {avatarInitial}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: theme => ({
                  mt: 1,
                  minWidth: 220,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                  boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.12)}`,
                }),
              },
            }}
          >
            <MenuItem
              onClick={() => { navigate('/account'); closeMenu(); }}
              sx={{ py: 1.25 }}
            >
              <ListItemIcon><Person fontSize='small' /></ListItemIcon>
              <ListItemText>Mein Benutzerkonto</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => { setHelpOpen(true); closeMenu(); }}
              sx={{ py: 1.25 }}
            >
              <ListItemIcon><HelpOutline fontSize='small' /></ListItemIcon>
              <ListItemText>Hilfe</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ py: 1.25, color: 'error.main' }}>
              <ListItemIcon><Logout fontSize='small' color='error' /></ListItemIcon>
              <ListItemText>Abmelden</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <HelpOverview
        configObject={configObject}
        isOpenParent={helpOpen}
        setIsOpenParent={setHelpOpen}
        disableButton
      />

      <Box
        component='main'
        sx={{ pt: '72px', minHeight: '100vh' }}
      >
        <Box
          sx={{
            maxWidth: 1600,
            mx: 'auto',
            px: { xs: 2, sm: 3, md: 4 },
            py: 3,
            pb: 6,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
